import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import Icon from "@insite/mobius/Icon";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries, injectCss } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { Collapsable, CollapsibleStyles } from "@ucs/Components/Collapsable";
import EmblaSlideShow, { EmblaImage } from "@ucs/Components/EmblaCarousel/EmblaSlideShow";
import ChevronDown from "@ucs/Icons/ChevronDownLarge";
import ChevronUp from "@ucs/Icons/ChevronUp";
import UCSColors from "@ucs/Utils/UCSColors";
import * as React from "react";
import { connect } from "react-redux";
import styled, { css } from "styled-components";

type Props = ReturnType<typeof mapStateToProps> & HasProductContext;

const mapStateToProps = (state: ApplicationState) => ({
    state,
});

const collapsibleStyles: CollapsibleStyles = {
    wrapper: {
        css: css`
            padding: 20px 0 0;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            padding: 0;
                        `,
                    ],
                    "min",
                );
            }}
        `,
    },
    header: {
        css: css`
            margin: 0 25px 10px;
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            display: none;
                        `,
                    ],
                    "min",
                );
            }}
        `,
    },
    content: {
        css: css`
            ${({ theme }) => {
                return breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            display: block;
                            height: 100%;
                        `,
                    ],
                    "min",
                );
            }}
        `,
    },
};

const CollapsibleWrapper = styled('div')<InjectableCss & {isShown: boolean}>`
    padding: 20px 25px;
    background-color: ${p => p.isShown ? "white" : "transparent"};
    ${injectCss}
`;
const CollapsibleHeader = styled.div<InjectableCss>`
    cursor: pointer;
    position: relative;
    ${injectCss}
`;
const CollapsibleContent = styled('div')<InjectableCss & {isShown: boolean}>`
    display: ${p => p.isShown ? "block": "none"};
    ${injectCss}
`;

const ProductImagesCarousel: React.FC<Props> = ({ productContext: { product, productInfo }, state }: Props) => {
    const images: EmblaImage[] = [];

    if (product.images) {
        product.images.forEach(image => {
            if (image.name.toLowerCase().includes("_ss_")) {
                images.push({ name: image.name, url: image.largeImagePath, altText: image.imageAltText });
            }
        });
    }

    if (images.length === 0) {
        images.push({ name: "fallback", url: "/UserFiles/Images/fallback.png" });
    }

    images.sort((a, b) => {
        return a.name!.localeCompare(b.name!);
    });

    const [collapsed, setCollapsed]  = React.useState(true);

    return (
        <CollapsibleWrapper isShown={!collapsed} {...collapsibleStyles?.wrapper}>
            <CollapsibleHeader onClick={() => setCollapsed(!collapsed)} {...collapsibleStyles?.header}>
                <Typography as="h3" variant="h3">{translate("Product Images")}</Typography>
                <Icon size={24} color={UCSColors.gray04} css={css`position: absolute; top:0px; bottom: 0px; right: 15px; margin: auto;`} src={collapsed ? ChevronDown : ChevronUp} />
            </CollapsibleHeader>
            <CollapsibleContent isShown={!collapsed} {...collapsibleStyles?.content}>
                <EmblaSlideShow id="products" images={images} />
            </CollapsibleContent>
        </CollapsibleWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: withProductContext(connect(mapStateToProps)(ProductImagesCarousel)),
    definition: {
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
